import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import Page from '../components/Page';
import Iconify from '../components/Iconify';
import AdminLogoutButton from '../components/AdminLogoutButton';
import useAuth from '../hooks/useAuth';
import { PATH_DASHBOARD, PATH_GIFTCARD } from '../routes/paths';

const APP_ROUTE_MAP = {
  card_redepmtion: PATH_GIFTCARD.orders,
  admin: PATH_DASHBOARD.dashboard.root,
  system_management: PATH_DASHBOARD.dashboard.root,
};

const APP_ICON_MAP = {
  card_redepmtion: 'solar:gift-bold',
  admin: 'solar:shield-user-bold',
  system_management: 'solar:shield-user-bold',
};

export default function AppSelector() {
  const { apps, user, currentAdmin } = useAuth();

  const availableApps = useMemo(
    () =>
      (apps || []).map((app) => ({
        ...app,
        route: APP_ROUTE_MAP[app.key],
        icon: APP_ICON_MAP[app.key] || 'solar:widget-5-bold',
      })),
    [apps]
  );

  const displayName =
    [user?.firstname || user?.first_name, user?.lastname || user?.last_name].filter(Boolean).join(' ') ||
    currentAdmin?.staff_id ||
    'Admin User';

  return (
    <Page title="App Selector">
      <Box
        sx={{
          minHeight: '100vh',
          background:
            'radial-gradient(circle at top, rgba(22,163,74,0.10), transparent 32%), linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
          display: 'flex',
          alignItems: 'center',
          py: 6,
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={4}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Chip
                  label="Admin App Selector"
                  color="success"
                  variant="outlined"
                  sx={{ mb: 2, fontWeight: 700 }}
                />
                <Typography variant="h3" sx={{ mb: 1 }}>
                  Choose an app
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Signed in as {displayName}
                </Typography>
              </Box>

              <AdminLogoutButton variant="contained" color="primary" />
            </Stack>

            <Grid container spacing={3} justifyContent="center">
              {availableApps.map((app) => {
                const enabled = true; //  Boolean(app.enabled && app.route);

                return (
                  <Grid item xs={12} sm={6} md={5} lg={4} key={app.key}>
                    <Card
                      component={enabled ? RouterLink : 'div'}
                      to={enabled ? app.route : undefined}
                      sx={{
                        minHeight: 240,
                        p: 3.5,
                        borderRadius: 4,
                        textDecoration: 'none',
                        color: 'text.primary',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.08)',
                        background: (theme) =>
                          `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.98)} 0%, ${alpha(
                            theme.palette.success.light,
                            0.08
                          )} 100%)`,
                        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                        ...(enabled && {
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 30px 70px rgba(15, 23, 42, 0.14)',
                            borderColor: 'success.main',
                          },
                        }),
                        ...(!enabled && {
                          opacity: 0.6,
                        }),
                      }}
                    >
                      <Stack spacing={2.5}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 3,
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: 'success.lighter',
                            color: 'success.dark',
                          }}
                        >
                          <Iconify icon={app.icon} width={28} height={28} />
                        </Box>

                        <Box>
                          <Typography variant="h5" sx={{ mb: 1 }}>
                            {app.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {app.key === 'card_redepmtion'
                              ? 'Review, claim, and complete card redemption orders.'
                              : 'Open the system management modules for governed admin operations.'}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Chip
                          size="small"
                          color={enabled ? 'success' : 'default'}
                          label={enabled ? 'Available' : 'Unavailable'}
                          sx={{ fontWeight: 700 }}
                        />
                        <Iconify icon="solar:arrow-right-up-linear" width={20} height={20} />
                      </Stack>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </Container>
      </Box>
    </Page>
  );
}
